/* ==========================================================================
   POSSEN — phone and tablet menu (snippets/px-menu.liquid)

   Each [data-px-menu-toggle] opens the sheet its aria-controls names. The
   sheet is a modal dialog under the header bar; the bar stays visible and
   its toggle is the close button (aria-expanded drives the bars → X).

   What it takes care of, so nobody notices:
     - the sheet starts exactly at the header's bottom edge, measured on
       open (the shop header and the brand header differ, and the shop
       header can be partly scrolled away)
     - the page behind cannot scroll (html.px-menu-open); Lenis, where it
       runs, sees the lock and stops (shop-editorial.js)
     - focus goes to the sheet, not to the search field (that would pop the
       phone keyboard over the menu), Tab cycles between the toggle and the
       current panel, Escape closes and returns focus to the toggle
     - a category panel takes focus on its back button; going back returns
       focus to the category it came from
     - widening the window past the point where the toggle is hidden closes
       the menu; a page restored from the back/forward cache comes back
       closed
   ========================================================================== */

(function () {
  'use strict';

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var PANEL_MS = 340;
  var CLOSE_MS = 170;

  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function setup(toggle) {
    var menu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!menu || toggle.__pxMenu) return;
    toggle.__pxMenu = true;

    var root = menu.querySelector('[data-px-menu-panel="root"]');
    var current = root;
    var opener = null;
    var isOpen = false;
    var closeTimer = null;

    function ms(n) {
      return reduceMQ.matches ? 0 : n;
    }

    function place() {
      var bar = toggle.closest('header');
      var bottom = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
      menu.style.setProperty('--px-menu-top', bottom + 'px');
    }

    function panels() {
      return Array.prototype.slice.call(menu.querySelectorAll('[data-px-menu-panel]'));
    }

    function focusables(panel) {
      return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), visible);
    }

    function reset() {
      panels().forEach(function (p) {
        var isRoot = p === root;
        p.hidden = !isRoot;
        p.classList.toggle('is-current', isRoot);
        p.classList.remove('is-behind');
      });
      menu.querySelectorAll('[data-px-menu-open]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
      var scroller = root.querySelector('.PxMenu__scroll');
      if (scroller) scroller.scrollTop = 0;
      current = root;
      opener = null;
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      window.clearTimeout(closeTimer);
      reset();
      place();
      menu.hidden = false;
      /* Commit the closed styles first, or the fade has nothing to start
         from. */
      menu.getBoundingClientRect();
      menu.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('px-menu-open');
      menu.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onResize);
    }

    function close(returnFocus) {
      if (!isOpen) return;
      isOpen = false;
      menu.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('px-menu-open');
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      closeTimer = window.setTimeout(function () {
        menu.hidden = true;
        reset();
      }, ms(CLOSE_MS));
      if (returnFocus) toggle.focus({ preventScroll: true });
    }

    function go(panel, trigger) {
      var from = current;
      if (!panel || panel === from) return;
      var back = panel === root;

      panel.hidden = false;
      panel.getBoundingClientRect();

      if (back) {
        from.classList.remove('is-current');
        panel.classList.remove('is-behind');
        panel.classList.add('is-current');
      } else {
        from.classList.add('is-behind');
        panel.classList.add('is-current');
        var scroller = panel.querySelector('.PxMenu__scroll');
        if (scroller) scroller.scrollTop = 0;
      }
      current = panel;

      /* The panel that left is hidden once it is out of sight, so Tab and
         screen readers only ever meet the one in view. */
      window.setTimeout(function () {
        if (current !== from) from.hidden = true;
      }, ms(PANEL_MS));

      if (back) {
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.focus({ preventScroll: true });
        }
        opener = null;
      } else {
        opener = trigger || null;
        if (opener) opener.setAttribute('aria-expanded', 'true');
        var backBtn = panel.querySelector('[data-px-menu-back]');
        if (backBtn) backBtn.focus({ preventScroll: true });
      }
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
        return;
      }
      if (e.key !== 'Tab') return;
      var list = [toggle].concat(focusables(current));
      var i = list.indexOf(document.activeElement);
      if (document.activeElement === menu || i === -1) {
        e.preventDefault();
        (e.shiftKey ? list[list.length - 1] : (list[1] || list[0])).focus();
        return;
      }
      if (e.shiftKey && i === 0) {
        e.preventDefault();
        list[list.length - 1].focus();
      } else if (!e.shiftKey && i === list.length - 1) {
        e.preventDefault();
        list[0].focus();
      }
    }

    function onResize() {
      if (!visible(toggle)) close(false);
      else place();
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOpen) close(true);
      else open();
    });

    menu.addEventListener('click', function (e) {
      var opens = e.target.closest('[data-px-menu-open]');
      if (opens) {
        go(document.getElementById(opens.getAttribute('data-px-menu-open')), opens);
        return;
      }
      if (e.target.closest('[data-px-menu-back]')) go(root);
    });

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) close(false);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-px-menu-toggle]').forEach(setup);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
